import type { Key } from "@heroui/react";
import {
    Popover,
    PopoverTrigger,
    PopoverContent,
    RangeCalendar,
    Label,
    Select,
    ListBox,
} from "@heroui/react";
import { CalendarDate, parseDate } from "@internationalized/date";
import type { RangeValue } from "@react-types/shared";
import { Calendar, ChevronDown } from "@gravity-ui/icons";
import { useMemo } from "react";
import type { EventFilters } from "../../types/eventFilters";
import { EVENT_TYPE_ITEMS, WEEKDAY_ITEMS } from "./constants";
import {
    formatDateRangeShort,
    eventTypeEmoji,
} from "../../utils/eventDisplay";
import {
    applyThisWeekendFilter,
    applyTodayFilter,
    clearDatePreset,
    isThisWeekendFilter,
    isTodayFilter,
} from "../../utils/eventFilterPresets";
import { categoryColor } from "../../utils/eventDisplay";
import type { EventType } from "../../reducers/apiTypes";

export type UiFilters = {
    timeOfDay: "morning" | "afternoon" | "evening" | null;
    freeOnly: boolean;
};

type Props = {
    filters: EventFilters;
    uiFilters: UiFilters;
    onChange: (next: EventFilters) => void;
    onUiChange: (next: UiFilters) => void;
    viewMode: "map" | "calendar";
    onViewModeChange: (next: "map" | "calendar") => void;
    eventCount?: number;
};

function filtersToRange(
    filters: EventFilters
): RangeValue<CalendarDate> | null {
    if (!filters.fromDate || !filters.toDate) return null;
    try {
        return {
            start: parseDate(filters.fromDate),
            end: parseDate(filters.toDate),
        };
    } catch {
        return null;
    }
}

function formatCalendarDateMonthDay(date: CalendarDate): string {
    const js = new Date(date.year, date.month - 1, date.day);
    return new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
    }).format(js);
}

const TIME_OPTIONS = [
    { id: "morning" as const, label: "Morning" },
    { id: "afternoon" as const, label: "Afternoon" },
    { id: "evening" as const, label: "Evening" },
];

function weekdaySelectLabel(weekdays: number[]): string {
    if (!weekdays.length) return "Any day";
    const names = WEEKDAY_ITEMS.filter((item) =>
        weekdays.includes(Number(item.id))
    ).map((item) => item.name);
    if (names.length <= 2) return names.join(", ");
    return `${names.length} days`;
}

function WeekdaySelect({
    filters,
    onChange,
    className = "",
}: {
    filters: EventFilters;
    onChange: (next: EventFilters) => void;
    className?: string;
}) {
    const selectedKeys = filters.weekdays.map(String);

    return (
        <Select
            aria-label="Filter by weekday"
            className={className}
            placeholder="Any day"
            selectionMode="multiple"
            value={selectedKeys}
            onChange={(keys) => {
                const weekdays = (keys as Key[])
                    .map((key) => Number(key))
                    .filter((n) => n >= 1 && n <= 7)
                    .sort((a, b) => a - b);
                onChange({ ...filters, weekdays });
            }}
        >
            <Select.Trigger className="w-full rounded-full bg-surface-secondary px-4 py-2 text-sm font-medium text-foreground md:w-auto">
                <Select.Value>{weekdaySelectLabel(filters.weekdays)}</Select.Value>
                <Select.Indicator />
            </Select.Trigger>
            <Select.Popover>
                <ListBox selectionMode="multiple">
                    {WEEKDAY_ITEMS.map((item) => (
                        <ListBox.Item
                            key={item.id}
                            id={item.id}
                            textValue={item.name}
                        >
                            {item.name}
                            <ListBox.ItemIndicator />
                        </ListBox.Item>
                    ))}
                </ListBox>
            </Select.Popover>
        </Select>
    );
}

function DatePresetPills({
    filters,
    onChange,
    className = "",
}: {
    filters: EventFilters;
    onChange: (next: EventFilters) => void;
    className?: string;
}) {
    const todayActive = isTodayFilter(filters);
    const weekendActive = isThisWeekendFilter(filters);

    const toggleToday = () => {
        onChange(todayActive ? clearDatePreset(filters) : applyTodayFilter(filters));
    };

    const toggleWeekend = () => {
        onChange(
            weekendActive
                ? clearDatePreset(filters)
                : applyThisWeekendFilter(filters)
        );
    };

    return (
        <div className={`flex flex-wrap gap-2 ${className}`}>
            <button
                type="button"
                aria-pressed={todayActive}
                onClick={toggleToday}
                className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                    todayActive
                        ? "bg-foreground text-white"
                        : "bg-surface-secondary text-foreground hover:bg-surface-tertiary"
                }`}
            >
                Today
            </button>
            <button
                type="button"
                aria-pressed={weekendActive}
                onClick={toggleWeekend}
                className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                    weekendActive
                        ? "bg-foreground text-white"
                        : "bg-surface-secondary text-foreground hover:bg-surface-tertiary"
                }`}
            >
                This weekend
            </button>
        </div>
    );
}

function DateRangeDropdown({
    filters,
    onChange,
    className = "",
    fullWidth = false,
}: {
    filters: EventFilters;
    onChange: (next: EventFilters) => void;
    className?: string;
    fullWidth?: boolean;
}) {
    const range = filtersToRange(filters);

    const onRangeChange = (value: RangeValue<CalendarDate> | null) => {
        if (!value?.start || !value?.end) {
            onChange({ ...filters, fromDate: null, toDate: null });
            return;
        }
        onChange({
            ...filters,
            fromDate: value.start.toString(),
            toDate: value.end.toString(),
        });
    };

    const dateLabel = range
        ? `${formatCalendarDateMonthDay(range.start)} – ${formatCalendarDateMonthDay(range.end)}`
        : formatDateRangeShort(filters.fromDate, filters.toDate, {
              today: isTodayFilter(filters),
              weekend: isThisWeekendFilter(filters),
          });

    return (
        <Popover>
            <PopoverTrigger>
                <button
                    type="button"
                    className={`inline-flex items-center gap-2 rounded-full bg-surface-secondary px-4 py-2 text-sm font-medium text-foreground transition hover:bg-surface-tertiary ${fullWidth ? "w-full justify-center" : ""} ${className}`}
                >
                    <Calendar className="size-4 text-muted" />
                    {dateLabel}
                    <ChevronDown className="size-4 text-muted" />
                </button>
            </PopoverTrigger>
            <PopoverContent className="p-0">
                <RangeCalendar
                    aria-label="Date range"
                    className="min-w-[min(100vw-2rem,22rem)] gap-2 p-4"
                    value={range ?? undefined}
                    onChange={(v) => onRangeChange(v ?? null)}
                >
                    <RangeCalendar.Header className="mb-2 gap-3 px-0 py-1">
                        <RangeCalendar.NavButton slot="previous" />
                        <RangeCalendar.Heading className="text-base font-semibold" />
                        <RangeCalendar.NavButton slot="next" />
                    </RangeCalendar.Header>
                    <RangeCalendar.Grid weekdayStyle="short" className="w-full">
                        <RangeCalendar.GridHeader>
                            {(day) => (
                                <RangeCalendar.HeaderCell className="pb-2 text-xs font-semibold uppercase">
                                    {day}
                                </RangeCalendar.HeaderCell>
                            )}
                        </RangeCalendar.GridHeader>
                        <RangeCalendar.GridBody>
                            {(date) => <RangeCalendar.Cell date={date} />}
                        </RangeCalendar.GridBody>
                    </RangeCalendar.Grid>
                </RangeCalendar>
            </PopoverContent>
        </Popover>
    );
}

function EventTypePills({
    filters,
    onChange,
}: {
    filters: EventFilters;
    onChange: (next: EventFilters) => void;
}) {
    const toggle = (typeId: string) => {
        const next = filters.eventTypes.includes(typeId)
            ? filters.eventTypes.filter((t) => t !== typeId)
            : [...filters.eventTypes, typeId].sort();
        onChange({ ...filters, eventTypes: next });
    };

    return (
        <div className="flex flex-wrap gap-2">
            {EVENT_TYPE_ITEMS.map((item) => {
                const selected = filters.eventTypes.includes(item.id);
                const color = categoryColor(item.id as EventType);
                const dotColors: Record<string, string> = {
                    pink: "bg-[#e879a8]",
                    green: "bg-[#4ade80]",
                    orange: "bg-[#f97316]",
                    purple: "bg-[#a78bfa]",
                    blue: "bg-[#60a5fa]",
                };
                return (
                    <button
                        key={item.id}
                        type="button"
                        aria-pressed={selected}
                        onClick={() => toggle(item.id)}
                        className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
                            selected
                                ? "bg-foreground text-white"
                                : "bg-surface-secondary text-foreground hover:bg-surface-tertiary"
                        }`}
                    >
                        {!selected && (
                            <span className={`size-1.5 rounded-full ${dotColors[color]}`} />
                        )}
                        <span aria-hidden>{eventTypeEmoji(item.id)}</span>
                        {item.name}
                    </button>
                );
            })}
        </div>
    );
}

export function EventFiltersBar({
    filters,
    uiFilters,
    onChange,
    onUiChange,
    viewMode,
    onViewModeChange,
    eventCount,
}: Props) {
    const activeFilterCount = useMemo(() => {
        let count = 0;
        if (filters.weekdays.length) count++;
        if (
            (filters.fromDate || filters.toDate) &&
            !isTodayFilter(filters) &&
            !isThisWeekendFilter(filters)
        )
            count++;
        else if (isTodayFilter(filters) || isThisWeekendFilter(filters)) count++;
        if (filters.eventTypes.length) count++;
        if (uiFilters.timeOfDay) count++;
        if (uiFilters.freeOnly) count++;
        return count;
    }, [filters, uiFilters]);

    const filterPanel = (
        <>
            <div className="hidden items-center gap-3 md:flex">
                <span className="text-xs font-semibold uppercase tracking-wide text-muted">
                    Day
                </span>
                <WeekdaySelect filters={filters} onChange={onChange} />
            </div>

            <div className="hidden items-center gap-2 md:flex">
                <DatePresetPills filters={filters} onChange={onChange} />
                <DateRangeDropdown filters={filters} onChange={onChange} />
            </div>

            <div className="hidden items-center gap-2 md:flex">
                {TIME_OPTIONS.map(({ id, label }) => {
                    const selected = uiFilters.timeOfDay === id;
                    return (
                        <button
                            key={id}
                            type="button"
                            aria-pressed={selected}
                            onClick={() =>
                                onUiChange({
                                    ...uiFilters,
                                    timeOfDay: selected ? null : id,
                                })
                            }
                            className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                                selected
                                    ? "bg-foreground text-white"
                                    : "bg-surface-secondary text-foreground hover:bg-surface-tertiary"
                            }`}
                        >
                            {label}
                        </button>
                    );
                })}
            </div>

            <Popover>
                <PopoverTrigger>
                    <button
                        type="button"
                        className="hidden items-center gap-1.5 rounded-full bg-surface-secondary px-4 py-2 text-sm font-medium text-foreground md:inline-flex"
                    >
                        Event type
                        <ChevronDown className="size-4 text-muted" />
                    </button>
                </PopoverTrigger>
                <PopoverContent className="max-w-sm p-4">
                    <EventTypePills filters={filters} onChange={onChange} />
                </PopoverContent>
            </Popover>

            <div className="hidden rounded-full bg-surface-secondary p-1 md:flex">
                <button
                    type="button"
                    aria-pressed={!uiFilters.freeOnly}
                    onClick={() => onUiChange({ ...uiFilters, freeOnly: false })}
                    className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                        !uiFilters.freeOnly
                            ? "bg-accent-green/60 text-foreground"
                            : "text-muted hover:text-foreground"
                    }`}
                >
                    All
                </button>
                <button
                    type="button"
                    aria-pressed={uiFilters.freeOnly}
                    onClick={() => onUiChange({ ...uiFilters, freeOnly: true })}
                    className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                        uiFilters.freeOnly
                            ? "bg-accent-green/60 text-foreground"
                            : "text-muted hover:text-foreground"
                    }`}
                >
                    Free
                </button>
            </div>
        </>
    );

    return (
        <>
            <div className="flex flex-col gap-3 px-4 pb-3 md:px-8 md:pb-4">
                {/* Mobile toolbar */}
                <div className="flex items-center gap-2 md:hidden">
                    <FilterModalButton
                        activeFilterCount={activeFilterCount}
                        filters={filters}
                        uiFilters={uiFilters}
                        onChange={onChange}
                        onUiChange={onUiChange}
                        eventCount={eventCount}
                    />
                    <DateRangeDropdown
                        filters={filters}
                        onChange={onChange}
                        className="shrink-0"
                    />
                    <ViewModeToggle
                        viewMode={viewMode}
                        onViewModeChange={onViewModeChange}
                        className="ml-auto"
                    />
                </div>

                {/* Desktop toolbar */}
                <div className="hidden items-center gap-3 md:flex">
                    {filterPanel}
                    <ViewModeToggle
                        viewMode={viewMode}
                        onViewModeChange={onViewModeChange}
                        className="ml-auto"
                    />
                </div>
            </div>
        </>
    );
}

function ViewModeToggle({
    viewMode,
    onViewModeChange,
    className = "",
}: {
    viewMode: "map" | "calendar";
    onViewModeChange: (next: "map" | "calendar") => void;
    className?: string;
}) {
    return (
        <div
            className={`flex rounded-full bg-surface-secondary p-1 ${className}`}
            role="group"
            aria-label="View mode"
        >
            <button
                type="button"
                aria-pressed={viewMode === "calendar"}
                onClick={() => onViewModeChange("calendar")}
                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
                    viewMode === "calendar"
                        ? "bg-white text-foreground shadow-sm"
                        : "text-muted hover:text-foreground"
                }`}
            >
                <Calendar className="size-4" />
                <span className="hidden sm:inline">Calendar</span>
            </button>
            <button
                type="button"
                aria-pressed={viewMode === "map"}
                onClick={() => onViewModeChange("map")}
                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
                    viewMode === "map"
                        ? "bg-white text-foreground shadow-sm"
                        : "text-muted hover:text-foreground"
                }`}
            >
                <svg className="size-4" viewBox="0 0 16 16" fill="currentColor" aria-hidden>
                    <path d="M8 1a4 4 0 0 0-4 4c0 2.5 4 9 4 9s4-6.5 4-9a4 4 0 0 0-4-4zm0 5.5a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3z" />
                </svg>
                <span className="hidden sm:inline">Map</span>
            </button>
        </div>
    );
}

function FilterModalButton({
    activeFilterCount,
    filters,
    uiFilters,
    onChange,
    onUiChange,
    eventCount,
}: {
    activeFilterCount: number;
    filters: EventFilters;
    uiFilters: UiFilters;
    onChange: (next: EventFilters) => void;
    onUiChange: (next: UiFilters) => void;
    eventCount?: number;
}) {
    return (
        <Popover>
            <PopoverTrigger>
                <button
                    type="button"
                    className="relative flex flex-1 items-center justify-center gap-2 rounded-full bg-surface-secondary px-4 py-2.5 text-sm font-semibold text-foreground"
                >
                    <svg className="size-4" viewBox="0 0 16 16" fill="currentColor" aria-hidden>
                        <path d="M1 3h14l-5 6v5l-4-2V9L1 3z" />
                    </svg>
                    Filters
                    {activeFilterCount > 0 && (
                        <span className="absolute -right-1 -top-1 flex size-5 items-center justify-center rounded-full bg-accent-orange text-[0.65rem] font-bold text-white">
                            {activeFilterCount}
                        </span>
                    )}
                </button>
            </PopoverTrigger>
            <PopoverContent className="fixed inset-x-4 bottom-4 top-auto max-h-[85vh] w-auto overflow-y-auto rounded-3xl p-6 md:hidden">
                <div className="mb-6 flex items-center justify-between">
                    <h2 className="text-2xl font-bold text-foreground">Filters</h2>
                    <button
                        type="button"
                        onClick={() => {
                            onChange({
                                ...filters,
                                weekdays: [],
                                fromDate: null,
                                toDate: null,
                                eventTypes: [],
                            });
                            onUiChange({ timeOfDay: null, freeOnly: false });
                        }}
                        className="text-sm font-medium text-muted hover:text-foreground"
                    >
                        Reset
                    </button>
                </div>

                <div className="flex flex-col gap-6">
                    <div>
                        <Label className="mb-3 block text-sm font-semibold text-foreground">
                            Weekday
                        </Label>
                        <WeekdaySelect
                            filters={filters}
                            onChange={onChange}
                            className="w-full"
                        />
                    </div>

                    <div>
                        <Label className="mb-3 block text-sm font-semibold text-foreground">
                            When
                        </Label>
                        <DatePresetPills
                            filters={filters}
                            onChange={onChange}
                            className="mb-3"
                        />
                        <DateRangeDropdown
                            filters={filters}
                            onChange={onChange}
                            fullWidth
                        />
                    </div>

                    <div>
                        <Label className="mb-3 block text-sm font-semibold text-foreground">
                            Time of day
                        </Label>
                        <div className="flex flex-wrap gap-2">
                            {TIME_OPTIONS.map(({ id, label }) => {
                                const selected = uiFilters.timeOfDay === id;
                                return (
                                    <button
                                        key={id}
                                        type="button"
                                        aria-pressed={selected}
                                        onClick={() =>
                                            onUiChange({
                                                ...uiFilters,
                                                timeOfDay: selected ? null : id,
                                            })
                                        }
                                        className={`rounded-full px-4 py-2 text-sm font-medium ${
                                            selected
                                                ? "bg-foreground text-white"
                                                : "bg-surface-secondary text-foreground"
                                        }`}
                                    >
                                        {label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div>
                        <Label className="mb-3 block text-sm font-semibold text-foreground">
                            Event type
                        </Label>
                        <EventTypePills filters={filters} onChange={onChange} />
                    </div>

                    <div>
                        <Label className="mb-3 block text-sm font-semibold text-foreground">
                            Cost
                        </Label>
                        <div className="flex gap-2">
                            <button
                                type="button"
                                aria-pressed={uiFilters.freeOnly}
                                onClick={() =>
                                    onUiChange({ ...uiFilters, freeOnly: true })
                                }
                                className={`flex-1 rounded-full px-4 py-3 text-sm font-semibold ${
                                    uiFilters.freeOnly
                                        ? "bg-accent-green/60 text-foreground"
                                        : "bg-surface-secondary text-foreground"
                                }`}
                            >
                                Free only
                            </button>
                            <button
                                type="button"
                                aria-pressed={!uiFilters.freeOnly}
                                onClick={() =>
                                    onUiChange({ ...uiFilters, freeOnly: false })
                                }
                                className={`flex-1 rounded-full px-4 py-3 text-sm font-semibold ${
                                    !uiFilters.freeOnly
                                        ? "bg-surface-secondary text-foreground ring-2 ring-foreground"
                                        : "bg-surface-secondary text-foreground"
                                }`}
                            >
                                All events
                            </button>
                        </div>
                    </div>
                </div>

                <button
                    type="button"
                    className="mt-6 w-full rounded-full bg-accent-orange py-3.5 text-base font-semibold text-white"
                >
                    Show {eventCount ?? 0} events
                </button>
            </PopoverContent>
        </Popover>
    );
}

export type { Props as EventFiltersBarProps };
